// OnboardingProgress.tsx

import type React from 'react';
import { FC, useMemo } from 'react';
import classNames from 'classnames';
import NextImage from 'next/image';

import { useAppSelector } from '@/hooks';
import { FlowModel } from '@/models/flow.model';

import styles from './OnboardingProgress.module.scss';
export type StepType = {
  id: string;
};
type OnboardingProgressProps = {
  steps: StepType[]; // passed directly from parent
  flow: FlowModel;
} & React.AllHTMLAttributes<unknown>;

export const OnboardingProgress: FC<OnboardingProgressProps> = ({
  className,
  steps,
  ...allAttr
}) => {
  const currentStep = useAppSelector(state => state.onboarding.currentStep);
  const currentStepIndex = useMemo(() => {
    const index = steps.findIndex(step => step.id === currentStep?.id);
    return index === -1 ? 0 : index;
  }, [steps, currentStep]);
  const progressVal = Math.round(((currentStepIndex + 1) / steps.length) * 100);

  return (
    <div
      className={ classNames(styles.OnboardingProgress, className) }
      { ...allAttr }
    >
      <div className={ styles.OnboardingProgress__Content }>
        <NextImage src="/assets/logo-full.svg" width={ 150 } height={ 32 } alt="TD Logo" />
        <span id="progress-label" className="color-secondary-brown ft-size-14">
          Step { currentStepIndex + 1 } of { steps.length }
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuenow={ progressVal }
        aria-valuemin={ 0 }
        aria-valuemax={ 100 }
        aria-labelledby="progress-label"
        className={ styles.progressBar }
      >
        { steps.map((step, index) => (
          <div
            key={ step.id }
            className={ classNames(styles.progressBar__Segment, {
              [styles.progressBar__Segment_filled]: index <= currentStepIndex,
              [styles.progressBar__Segment_first]: index === 0,
              [styles.progressBar__Segment_last]: index === steps.length - 1,
            }) }
          />
        )) }
      </div>
    </div>
  );
};
